import React, { useState } from "react";
import { Library, Plus, X } from "lucide-react";
import { toast } from "sonner";

import { BrandButton, IconButton } from "@/shared/components/ui-kit";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { Switch } from "@/shared/components/ui/switch";
import { useKnowledgeBases, useLinkKbAgents, useUnlinkKbAgent } from "@/shared/api/knowledge";

import type { KbLinkMode, KnowledgeBase } from "@/shared/types";

/* The agent's default knowledge bases. A link is live — it never goes through
   the agent's draft and publish — so each click writes straight to the server. */

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong");

function Picker({ agentId, choices, onClose }: { agentId: string; choices: KnowledgeBase[]; onClose: () => void }) {
  const [picked, setPicked] = useState<string[]>([]);
  const link = useLinkKbAgents();

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const confirm = async () => {
    const results = await Promise.allSettled(picked.map((kbId) => link.mutateAsync({ kbId, agentIds: [agentId] })));
    const failed = results.find((r): r is PromiseRejectedResult => r.status === "rejected");
    if (failed) toast.error(errorText(failed.reason));
    else onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[440px] p-0 gap-0 rounded-[16px] bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog overflow-hidden">
        <div className="px-5 pt-5 pb-3 border-b hairline">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            Add knowledge base
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
            New chats with this agent start with the bases you pick.
          </DialogDescription>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {choices.length === 0 && (
            <div className="px-5 py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">
              Every knowledge base is already added
            </div>
          )}
          {choices.map((b) => (
            <label key={b.id} className="flex items-center gap-2.5 px-5 py-2 cursor-pointer hover:bg-[var(--surface-sunk)]/45">
              <input type="checkbox" checked={picked.includes(b.id)} onChange={() => toggle(b.id)} />
              <span className="min-w-0 flex-1 text-[13px] font-medium text-[var(--text-primary)] truncate" title={b.name}>{b.name}</span>
              <span className="shrink-0 whitespace-nowrap text-[11.5px] text-[var(--text-tertiary)]">
                {b.file_count} {b.file_count === 1 ? "file" : "files"}
              </span>
            </label>
          ))}
        </div>
        <div className="px-5 py-3.5 border-t hairline flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="press h-8 px-3.5 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
          >
            Cancel
          </button>
          <BrandButton onClick={confirm} disabled={!picked.length || link.isPending} className="disabled:opacity-40 disabled:pointer-events-none">
            Add{picked.length > 0 && ` ${picked.length}`}
          </BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* One linked base. The switch shows the wanted mode while the write is in flight,
   and falls back to the server's on failure. */
function KbRow({ base, agentId, onUnlink }: {
  base: KnowledgeBase;
  agentId: string;
  onUnlink: () => void;
}) {
  const link = useLinkKbAgents();
  const [wanted, setWanted] = useState<KbLinkMode | null>(null);
  const writes = (wanted ?? base.mode) === "read_write";
  const setMode = (on: boolean) => {
    const mode: KbLinkMode = on ? "read_write" : "read";
    setWanted(mode);
    link.mutate(
      { kbId: base.id, agentIds: [agentId], mode },
      { onError: (e) => toast.error(errorText(e)), onSettled: () => setWanted(null) },
    );
  };

  return (
    <div className="group px-2 py-[7px] rounded-[8px] hover:bg-[var(--surface)] min-w-0">
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="w-5 h-5 shrink-0 rounded-[5px] bg-[var(--surface-2)] flex items-center justify-center text-[12px]">
          {base.icon ?? <Library className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />}
        </span>
        <span title={base.name} className="min-w-0 flex-1 text-[13px] font-medium text-[var(--text-primary)] truncate">{base.name}</span>
        <span className="shrink-0 whitespace-nowrap text-[11.5px] text-[var(--text-tertiary)]">
          {base.file_count} {base.file_count === 1 ? "file" : "files"}
        </span>
        <IconButton onClick={onUnlink} title={`Unlink ${base.name}`} className="hover:text-[var(--danger)]">
          <X className="w-3 h-3" strokeWidth={1.75} />
        </IconButton>
      </div>
      <label
        title="Its chats may file findings into this base's notes"
        className="mt-1.5 pl-[30px] flex items-center justify-between gap-3 text-[12px] text-[var(--text-secondary)] cursor-pointer"
      >
        Agent can add notes
        <Switch
          aria-label={`Agent can add notes to ${base.name}`}
          checked={writes}
          disabled={link.isPending}
          onCheckedChange={setMode}
        />
      </label>
    </div>
  );
}

export function AgentKnowledge({ agentId }: { agentId: string }) {
  const { data: linked, isPending } = useKnowledgeBases(agentId);
  const { data: all } = useKnowledgeBases();
  const unlink = useUnlinkKbAgent();
  const [picking, setPicking] = useState(false);

  const rows = linked ?? [];
  const taken = new Set(rows.map((b) => b.id));
  const remove = (kbId: string) =>
    unlink.mutate({ kbId, agentId }, { onError: (e) => toast.error(errorText(e)) });

  return (
    <div className="py-3">
      {isPending && <div className="py-2 text-[12.5px] text-[var(--text-quaternary)]">Loading knowledge bases…</div>}
      {!isPending && rows.length === 0 && (
        <div className="py-2 text-[12.5px] text-[var(--text-quaternary)]">No knowledge bases yet</div>
      )}
      {rows.map((b) => (
        <KbRow key={b.id} base={b} agentId={agentId} onUnlink={() => remove(b.id)} />
      ))}
      <button
        onClick={() => setPicking(true)}
        className="press mt-2 w-full h-9 rounded-[8px] border border-dashed border-[var(--hairline-strong)] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:border-[var(--text-tertiary)] flex items-center justify-center gap-1.5"
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add knowledge base
      </button>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[12px] text-[var(--text-quaternary)]">
        <span>Applies immediately. New chats use these; a chat can change its own.</span>
        <a href="#/knowledge" className="shrink-0 whitespace-nowrap text-[var(--text-secondary)] hover:text-[var(--text-primary)] underline underline-offset-2">
          Manage knowledge bases
        </a>
      </div>
      {picking && (
        <Picker agentId={agentId} choices={(all ?? []).filter((b) => !taken.has(b.id))} onClose={() => setPicking(false)} />
      )}
    </div>
  );
}
